/**
 * REFACTOR-SPLIT: VAL-FILE-SPLIT-1 — ProfileCard and the Engagement form
 * fields moved out to _ProfileCard.tsx / _CreateEngagementForm.tsx (pure
 * move, no behaviour change). This file's line-count drop is that move.
 *
 * Screen 1 of 4 — PROFILE SELECT.
 *
 * The front door of the Business Valuator. Two jobs:
 *
 *   1. Show which SIGNED industry profile the engine would value against, with
 *      its governance record visible — version, who activated it, what it
 *      cites, and the signed multiple envelope it starts from. Picking an
 *      industry off a bare dropdown is picking a number you cannot see.
 *   2. Open the engagement, fixing `value_kind` at creation. That choice is
 *      the AICPA SSVS No. 1 deliverable gate and there is deliberately no PATCH
 *      path that promotes a calculation into a conclusion of value later.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { createValuationEngagement, fetchValuationProfiles, updateValuationEngagement } from '@/api/valuation'
import { formatApiError } from '@/utils/httpErrors'
import { todayCentral } from './_valuationDate'
import type { ValuationProfile, ValueKind } from '@/types/valuation'
import {
  ValuationStepper,
  useValuationEntitlement,
} from './_shared'
import { ProfileCard } from './_ProfileCard'
import { CreateEngagementForm } from './_CreateEngagementForm'

export default function ValuationProfileSelect() {
  const navigate = useNavigate()
  const { canConclude, role } = useValuationEntitlement()
  // JOIN-MDEMO 3d: R3 plain words for the seller only (OWNER_APPROVALS.log 2026-10-26 05:36); staff keep today's text.
  const isOwner = role === 'valuation_owner'

  const [profiles, setProfiles] = useState<ValuationProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [industry, setIndustry] = useState<string>('')
  // VAL-OVERVIEW-SKELETON-2 §4 amendment: the engagement form is the default
  // view; the profile gallery is a second view of this same wizard step,
  // opened from the "Methodology basis" row and never a route of its own.
  const [view, setView] = useState<'form' | 'gallery'>('form')
  const [companyName, setCompanyName] = useState('')
  const [periodLabel, setPeriodLabel] = useState('')
  const [effectiveDate, setEffectiveDate] = useState(() => todayCentral())
  const [valueKind, setValueKind] = useState<ValueKind>('calculated')
  const [submitting, setSubmitting] = useState(false)

  // VAL-INTAKE-REQUIRED-1: the five engagement-particulars fields the API
  // now requires at create — see REQUIRED_PARTICULARS_FIELDS in
  // core/valuation/finalize_particulars.py. Collected here so the signer
  // learns what's missing before opening the engagement, not at finalize.
  const [purpose, setPurpose] = useState('')
  const [intendedUsers, setIntendedUsers] = useState('')
  const [standardOfValue, setStandardOfValue] = useState('')
  const [premiseOfValue, setPremiseOfValue] = useState('')
  const [subjectInterest, setSubjectInterest] = useState('')

  useEffect(() => {
    let live = true
    fetchValuationProfiles()
      .then(profileRes => {
        if (!live) return
        setProfiles(profileRes.profiles)
      })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  const selectable = useMemo(() => profiles.filter(p => p.selectable), [profiles])
  const unsigned = useMemo(() => profiles.filter(p => !p.selectable), [profiles])
  // The Methodology basis row must read from THIS — the actual selected
  // profile object — never a hardcoded one (VAL-OVERVIEW-SKELETON-2 §4).
  const selectedProfile = useMemo(
    () => profiles.find(p => p.industry_code === industry) ?? null,
    [profiles, industry],
  )

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!companyName.trim()) { toast.error('Subject company name is required.'); return }
    if (!industry) { toast.error('Select an industry profile.'); return }
    if (!purpose.trim()) { toast.error('Purpose of the engagement is required.'); return }
    if (!intendedUsers.trim()) { toast.error('Intended users is required.'); return }
    if (!standardOfValue.trim()) { toast.error('Standard of value is required.'); return }
    if (!premiseOfValue.trim()) { toast.error('Premise of value is required.'); return }
    if (!subjectInterest.trim()) { toast.error('Subject interest is required.'); return }

    setSubmitting(true)
    try {
      const created = await createValuationEngagement({
        subject_company_name: companyName.trim(),
        industry,
        period_label: periodLabel.trim() || null,
        value_kind: valueKind,
        purpose: purpose.trim(),
        intended_users: intendedUsers.trim(),
        standard_of_value: standardOfValue.trim(),
        premise_of_value: premiseOfValue.trim(),
        subject_interest: subjectInterest.trim(),
      })
      // VAL-VALUATION-DATE-1: _CreateBody has no effective_date field — only
      // _UpdateBody does (R1.3, CPA-editable until finalize). Same
      // create-then-PATCH shape the row's own DB default relies on.
      if (effectiveDate) {
        await updateValuationEngagement(created.valuation_engagement_id, { effective_date: effectiveDate })
          .catch((err: unknown) => toast.error(formatApiError(err)))
      }
      toast.success(`Engagement opened — ${created.subject_company_name}`)
      navigate(`/valuation/${created.valuation_engagement_id}/company`)
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      setSubmitting(false)
    }
  }

  function pickProfile(code: string) {
    setIndustry(code)
    setView('form')
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-72" />
        </div>
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {loadError}
        </div>
      </div>
    )
  }

  if (view === 'gallery') {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Choose an industry profile</h1>
          <p className="mt-1 text-sm text-gray-500">
            Signed profiles the engine can value against, with governance and multiples visible.
          </p>
        </div>

        <ValuationStepper current="profile" />

        <div className="grid gap-4 md:grid-cols-2">
          {selectable.map(p => (
            <ProfileCard
              key={p.profile_id}
              profile={p}
              selected={industry === p.industry_code}
              onSelect={() => pickProfile(p.industry_code)}
            />
          ))}
        </div>
        {unsigned.length > 0 && (
          <div className="pt-2">
            <p className="mb-2 text-xs font-medium text-gray-500">
              Not yet switched on for your firm
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {unsigned.map(p => (
                <ProfileCard key={p.profile_id} profile={p} selected={false} onSelect={() => {}} />
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-start">
          <Button type="button" variant="outline" onClick={() => setView('form')}>
            Back to engagement
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Business Valuation</h1>
        <p className="mt-1 text-sm text-gray-500">
          {isOwner
            ? 'Tell us about the business, then choose its industry.'
            : 'Fill in the engagement, then open it against the signed industry profile below.'}
        </p>
      </div>

      <ValuationStepper current="profile" />

      <form onSubmit={handleCreate} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Engagement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CreateEngagementForm
              selectedProfile={selectedProfile}
              onChooseProfile={() => setView('gallery')}
              companyName={companyName} setCompanyName={setCompanyName}
              periodLabel={periodLabel} setPeriodLabel={setPeriodLabel}
              effectiveDate={effectiveDate} setEffectiveDate={setEffectiveDate}
              purpose={purpose} setPurpose={setPurpose}
              intendedUsers={intendedUsers} setIntendedUsers={setIntendedUsers}
              standardOfValue={standardOfValue} setStandardOfValue={setStandardOfValue}
              premiseOfValue={premiseOfValue} setPremiseOfValue={setPremiseOfValue}
              subjectInterest={subjectInterest} setSubjectInterest={setSubjectInterest}
              valueKind={valueKind} setValueKind={setValueKind}
              canConclude={canConclude}
              isOwner={isOwner}
            />
          </CardContent>
        </Card>

        <div className="flex flex-col items-end gap-1">
          {!selectedProfile && (
            <p className="text-xs text-amber-800">Choose an industry profile before opening the engagement.</p>
          )}
          <Button type="submit" disabled={submitting || !selectedProfile}>
            {submitting ? 'Opening…' : 'Open engagement'}
          </Button>
        </div>
      </form>
    </div>
  )
}
